import React, { useMemo, useRef, useState } from 'react';

export default function CodePrintPage({
    title,
    mode,
    products,
    stores,
    onGenerate,
}) {
    const [selectedStore, setSelectedStore] = useState('');
    const [selectedProductId, setSelectedProductId] = useState('');
    const [paperSize, setPaperSize] = useState('');
    const [showStoreName, setShowStoreName] = useState(true);
    const [showProductName, setShowProductName] = useState(true);
    const [showPrice, setShowPrice] = useState(true);
    const [showReferenceNumber, setShowReferenceNumber] = useState(true);
    const [generated, setGenerated] = useState(null);
    const printRef = useRef(null);

    const filteredProducts = useMemo(() => (
        selectedStore
            ? products.filter((item) => String(item.store_id || '') === String(selectedStore))
            : products
    ), [products, selectedStore]);

    const selectedProduct = useMemo(
        () => filteredProducts.find((item) => String(item.id) === String(selectedProductId)),
        [filteredProducts, selectedProductId]
    );

    const selectedStoreName = useMemo(
        () => stores.find((item) => String(item.id) === String(selectedStore))?.name || 'Unassigned',
        [stores, selectedStore]
    );

    const handleGenerate = async () => {
        if (!selectedProduct) return;
        const result = await onGenerate(selectedProduct);
        setGenerated(result || null);
    };

    const reset = () => {
        setSelectedStore('');
        setSelectedProductId('');
        setPaperSize('');
        setGenerated(null);
    };

    const handlePrint = () => {
        if (!generated || !printRef.current) return;
        const win = window.open('', '_blank', 'width=420,height=620');
        if (!win) {
            window.alert('Please allow popups so the code can be printed.');
            return;
        }
        win.document.write(`
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${title}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; padding: 12px; font-family: Arial, sans-serif; color: #111827; }
  .code-print-only { width: 100%; display: grid; place-items: center; gap: 7px; text-align: center; }
  .code-preview-product-name { font-size: 13px; font-weight: 700; max-width: 260px; overflow-wrap: anywhere; }
  .code-preview-barcode-val { font-size: 11px; max-width: 260px; overflow-wrap: anywhere; }
  .code-preview-img { max-width: 260px; max-height: 180px; object-fit: contain; display: block; }
  @media print {
    @page { size: ${paperSize === 'receipt' ? '80mm auto' : paperSize === 'sticker' ? '58mm 40mm' : 'auto'}; margin: 4mm; }
    body { padding: 0; }
  }
</style>
</head>
<body>${printRef.current.outerHTML}</body>
</html>`);
        win.document.close();
        setTimeout(() => {
            win.focus();
            win.print();
            setTimeout(() => win.close(), 250);
        }, 150);
    };

    return (
        <div className="products-page">
            <div className="page-header products-header">
                <div>
                    <h2>{title}</h2>
                    <p>Dashboard <span className="crumb-sep">›</span> {title}</p>
                </div>
                <div className="products-toolbar-actions">
                    <button className="mini-icon-btn" type="button" onClick={reset}>↻</button>
                    <button className="mini-icon-btn" type="button">⌃</button>
                </div>
            </div>

            <section className="products-shell code-tool-shell">
                <div className="code-print-grid">
                    <div className="code-print-panel">
                        <h3 className="code-print-title">Selection</h3>
                        <div className="code-print-form">
                            <label>
                                <span>Store</span>
                                <select value={selectedStore} onChange={(e) => {
                                    setSelectedStore(e.target.value);
                                    setSelectedProductId('');
                                    setGenerated(null);
                                }}>
                                    <option value="">All stores</option>
                                    {stores.map((store) => (
                                        <option key={store.id} value={store.id}>{store.name}</option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <span>Product</span>
                                <select value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)}>
                                    <option value="">Select product</option>
                                    {filteredProducts.map((product) => (
                                        <option key={product.id} value={product.id}>{product.name} ({product.sku})</option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <span>Paper Size</span>
                                <select value={paperSize} onChange={(e) => setPaperSize(e.target.value)}>
                                    <option value="">Select</option>
                                    <option value="a4">A4</option>
                                    <option value="receipt">Receipt</option>
                                    <option value="sticker">Sticker</option>
                                </select>
                            </label>

                            <div className="code-switches">
                                {mode === 'barcode' ? (
                                    <>
                                        <label className="toggle-row"><span>Show Store Name</span><input type="checkbox" checked={showStoreName} onChange={() => setShowStoreName((v) => !v)} /></label>
                                        <label className="toggle-row"><span>Show Product Name</span><input type="checkbox" checked={showProductName} onChange={() => setShowProductName((v) => !v)} /></label>
                                        <label className="toggle-row"><span>Show Price</span><input type="checkbox" checked={showPrice} onChange={() => setShowPrice((v) => !v)} /></label>
                                    </>
                                ) : (
                                    <label className="toggle-row"><span>Reference Number</span><input type="checkbox" checked={showReferenceNumber} onChange={() => setShowReferenceNumber((v) => !v)} /></label>
                                )}
                            </div>

                            <div className="modal-footer code-tool-actions">
                                <button type="button" className="btn" onClick={handleGenerate} disabled={!selectedProduct}>{mode === 'barcode' ? 'Generate Barcode' : 'Generate QR Code'}</button>
                                <button type="button" className="btn btn-dark" onClick={reset}>Reset</button>
                                <button type="button" className="btn print-btn" onClick={handlePrint} disabled={!generated}>Print</button>
                            </div>
                        </div>
                    </div>

                    <div className="code-preview-panel">
                        <p className="barcode-preview-label">Selected Item</p>
                        <div className="code-print-only" ref={printRef}>
                            {selectedProduct ? (
                                <>
                                {mode === 'barcode' && showStoreName ? <div className="code-preview-barcode-val">{selectedStoreName}</div> : null}
                                {mode === 'barcode' && showProductName ? <div className="code-preview-product-name">{selectedProduct.name}</div> : null}
                                {mode === 'barcode' ? <div className="code-preview-barcode-val">{selectedProduct.barcode}</div> : null}
                                {mode === 'barcode' && showPrice ? <div className="code-preview-barcode-val">Price: ₦{Number(selectedProduct.selling_price || 0).toLocaleString('en-NG')}</div> : null}
                                {mode === 'qrcode' ? <div className="code-preview-product-name">{selectedProduct.name}</div> : null}
                                {mode === 'qrcode' && showReferenceNumber ? <div className="code-preview-barcode-val">Reference: {selectedProduct.product_id || selectedProduct.sku}</div> : null}
                                {generated ? <img className="code-preview-img" src={generated} alt={`${mode} preview`} /> : <p className="dash-empty-row">Generate a {mode === 'barcode' ? 'barcode' : 'QR code'} preview.</p>}
                                </>
                            ) : (
                                <p className="dash-empty-row">Select a store and product to continue.</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="products-table-wrap code-table-wrap">
                    <table className="products-table admin-table">
                        <thead>
                            <tr>
                                <th>Product</th>
                                <th>Store</th>
                                <th>SKU</th>
                                <th>Code</th>
                                {mode === 'qrcode' ? <th>Reference Number</th> : null}
                                <th>Qty</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProducts.length === 0 ? (
                                <tr>
                                    <td colSpan={mode === 'qrcode' ? 6 : 5} className="dash-empty-row">No products available</td>
                                </tr>
                            ) : filteredProducts.slice(0, 12).map((product) => (
                                <tr key={product.id}>
                                    <td>{product.name}</td>
                                    <td>{stores.find((store) => String(store.id) === String(product.store_id))?.name || 'Unassigned'}</td>
                                    <td>{product.sku}</td>
                                    <td>{mode === 'barcode' ? product.barcode : product.sku}</td>
                                    {mode === 'qrcode' ? <td>{product.product_id || product.sku}</td> : null}
                                    <td>{product.quantity_in_stock || 0}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
